import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

const LINKED_LINE_PROPERTY = '_linked_line';
const FOR_VARIANT_PROPERTY = '_for_variant';
const INSTALLATION_FOR_PROPERTY = '_installation_for';
const PARENT_PRODUCT_TITLE_PROPERTY = '_parent_product_title';
const FOR_PROPERTY = 'For';

let maintenancePromise = null;
let initialized = false;

function toProductGid(id) {
  if (id == null || id === '') return null;
  const value = String(id);
  if (value.startsWith('gid://')) return value;
  return `gid://shopify/Product/${value}`;
}

function hasParentVariantInCart(cartItems, variantId) {
  if (variantId == null || variantId === '') return false;

  const variantStr = String(variantId);
  return cartItems.some(
    (item) =>
      String(item.variant_id) === variantStr &&
      item.product_type !== 'Guarantee' &&
      !item.properties?.[INSTALLATION_FOR_PROPERTY],
  );
}

function buildParentKeyByVariant(cartItems) {
  const map = new Map();

  for (const item of cartItems) {
    if (item.product_type === 'Guarantee') continue;
    if (item.properties?.[INSTALLATION_FOR_PROPERTY]) continue;
    map.set(String(item.variant_id), item.key);
  }

  return map;
}

async function relinkGuaranteeItem(guarantee, expectedParentKey) {
  const removeResponse = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: guarantee.key, quantity: 0 }),
  });

  if (!removeResponse.ok) {
    throw new Error('Failed to remove guarantee for relink');
  }

  const addResponse = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: guarantee.variant_id,
          quantity: guarantee.quantity,
          properties: {
            [LINKED_LINE_PROPERTY]: expectedParentKey,
            [FOR_VARIANT_PROPERTY]: guarantee.properties?.[FOR_VARIANT_PROPERTY] ?? '',
            [PARENT_PRODUCT_TITLE_PROPERTY]:
              guarantee.properties?.[PARENT_PRODUCT_TITLE_PROPERTY] ?? '',
            [FOR_PROPERTY]: guarantee.properties?.[FOR_PROPERTY] ?? '',
          },
        },
      ],
    }),
  });

  const addResult = await addResponse.json();
  if (addResult.status) {
    throw new Error(addResult.description || 'Failed to re-add guarantee');
  }

  return fetchCart();
}

async function syncGuaranteeLinks(cart) {
  let currentCart = cart;
  let didSync = false;

  while (true) {
    const parentKeyByVariant = buildParentKeyByVariant(currentCart.items);
    const guarantee = currentCart.items.find((item) => {
      if (item.product_type !== 'Guarantee') return false;

      const forVariant = item.properties?.[FOR_VARIANT_PROPERTY];
      if (!forVariant) return false;

      const expectedParentKey = parentKeyByVariant.get(String(forVariant));
      if (!expectedParentKey) return false;

      return item.properties?.[LINKED_LINE_PROPERTY] !== expectedParentKey;
    });

    if (!guarantee) break;

    const forVariant = guarantee.properties?.[FOR_VARIANT_PROPERTY];
    const expectedParentKey = parentKeyByVariant.get(String(forVariant));

    currentCart = await relinkGuaranteeItem(guarantee, expectedParentKey);
    didSync = true;
  }

  return { cart: currentCart, didSync };
}

async function fetchCart() {
  const response = await fetch('/cart.js');
  if (!response.ok) throw new Error('Failed to fetch cart');

  const cart = await response.json();
  window.__cart = cart;
  return cart;
}

async function removeOrphanedGuarantees(cart) {
  let currentCart = cart || (await fetchCart());
  const { cart: syncedCart, didSync } = await syncGuaranteeLinks(currentCart);
  currentCart = syncedCart;

  const lineKeys = new Set(currentCart.items.map((item) => item.key));
  const parentProductGids = new Set(
    currentCart.items
      .filter((item) => !item.properties?.[INSTALLATION_FOR_PROPERTY])
      .map((item) => toProductGid(item.product_id))
      .filter(Boolean),
  );

  const orphaned = currentCart.items.filter((item) => {
    const linkedLine = item.properties?.[LINKED_LINE_PROPERTY];
    if (linkedLine && !lineKeys.has(linkedLine)) {
      if (
        item.product_type === 'Guarantee' &&
        hasParentVariantInCart(currentCart.items, item.properties?.[FOR_VARIANT_PROPERTY])
      ) {
        return false;
      }

      return true;
    }

    const installFor = item.properties?.[INSTALLATION_FOR_PROPERTY];
    if (installFor) {
      return !parentProductGids.has(toProductGid(installFor));
    }

    return false;
  });

  if (orphaned.length === 0) {
    return didSync ? currentCart : null;
  }

  const updates = Object.fromEntries(orphaned.map((item) => [item.key, 0]));

  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ updates }),
  });

  if (!response.ok) throw new Error('Failed to remove orphaned guarantees');

  const updatedCart = await response.json();
  window.__cart = updatedCart;
  return updatedCart;
}

export async function maintainGuaranteeLinks() {
  if (maintenancePromise) return maintenancePromise;

  maintenancePromise = (async () => {
    try {
      return await removeOrphanedGuarantees();
    } catch (error) {
      console.error('[CartGuaranteeCleanup] Failed to maintain guarantees', error);
      return null;
    } finally {
      maintenancePromise = null;
    }
  })();

  return maintenancePromise;
}

function initOrphanedGuaranteeCleanup() {
  if (initialized) return;
  initialized = true;

  ThemeBus.addEventListener(EVENTS.cartUpdated, () => {
    maintainGuaranteeLinks();
  });
}

window.Theme = window.Theme || {};
window.Theme.maintainGuaranteeLinks = maintainGuaranteeLinks;
initOrphanedGuaranteeCleanup();
